import React, { useCallback, useMemo } from "react";

import { usePropertyQuery } from "api/property/usePropertyQuery";
import type { ExternalServiceConfig as ExternalService } from "api/schemas";
import { useParams } from "shared/Router";

import ServicePortal from "./Portal";
import { serviceRenderers, type RendererContext } from "./registry";

export interface ExternalServicesProps {
  propertySlug?: string;
  portalIds?: Record<string, string>;
}

const ExternalServices = ({
  propertySlug,
  portalIds,
}: ExternalServicesProps) => {
  const params = useParams();
  const slug = propertySlug ?? params.propertySlug;

  const { data, isLoading, isError } = usePropertyQuery(slug, {
    enabled: !!slug,
  });

  const services = useMemo<ExternalService[]>(
    () => data?.data.external_services ?? [],
    [data?.data.external_services]
  );

  const rendererContext: RendererContext = useMemo(
    () => ({
      services,
      propertySlug: slug,
    }),
    [services, slug]
  );

  const portalIdFor = useCallback(
    (serviceType?: string) =>
      serviceType ? portalIds?.[serviceType] : undefined,
    [portalIds]
  );

  const rendered = services
    .map((service, index) => {
      const render = serviceRenderers[service.type];
      if (!render) return null;
      const node = render({ service, context: rendererContext });
      if (!node) return null;

      if (service.type === "GoogleAnalytics") {
        return (
          <React.Fragment key={`${service.type}-${index}`}>
            {node}
          </React.Fragment>
        );
      }

      const portalId = portalIdFor(service.type);
      if (!portalId) return null;

      return (
        <ServicePortal key={`${service.type}-${index}`} portalId={portalId}>
          {node}
        </ServicePortal>
      );
    })
    .filter(Boolean);

  if (isLoading || isError) {
    return null;
  }

  return <>{rendered}</>;
};

export default ExternalServices;
